import { useMemo, useState } from "react";
import {
  format,
  isSameDay,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isWithinInterval,
  subWeeks,
  subMonths,
} from "date-fns";
import { LineChart, Leaf, Lightbulb, ChevronDown } from "lucide-react";
import { useAiWeeklyNarrative } from "@/hooks/use-weekly-narrative";
import { useAiMonthlyNarrative } from "@/hooks/use-monthly-narrative";
import { coerceJournalTagForDisplay } from "@/lib/journal-tags";
import { cn } from "@/lib/utils";
import { InsightShell, InsightHeader } from "./insight-shell";
import { InsightCard } from "./insight-card";
import { TrendChart } from "./trend-chart";
import type { InsightJournal } from "./types";

type Granularity = "week" | "month";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * The single Insights card.
 *
 * Two axes, deliberately separated:
 *   • the calendar supplies the TIME CONTEXT (`referenceDate`)
 *   • this card's selector supplies the LEVEL OF ANALYSIS (week vs month)
 *
 * So selecting Aug 6 and choosing Week shows Mon–Sun around Aug 6; choosing
 * Month shows August 2026. Switching the selector never moves the calendar, and
 * moving the calendar never changes the selector.
 *
 * Honesty rules carried over from the two cards this replaces: counts, themes and
 * the trend are always local arithmetic; the "AI insight" badge appears only when
 * a model actually returned prose; the monthly view never invents prose when the
 * backend has none.
 */
export function InsightsCard({
  journals,
  referenceDate,
}: {
  journals: InsightJournal[];
  /** Day selected on the calendar, or the browsed month when nothing is selected. */
  referenceDate: Date;
}) {
  const [granularity, setGranularity] = useState<Granularity>("week");
  const [collapsed, setCollapsed] = useState(false);

  // ── Weekly (local) ────────────────────────────────────────────────────────
  const week = useMemo(() => {
    const start = startOfWeek(referenceDate, { weekStartsOn: 1 });
    const end = endOfWeek(referenceDate, { weekStartsOn: 1 });
    const prevStart = startOfWeek(subWeeks(referenceDate, 1), { weekStartsOn: 1 });
    const prevEnd = endOfWeek(subWeeks(referenceDate, 1), { weekStartsOn: 1 });

    const inWeek = journals.filter((j) =>
      isWithinInterval(new Date(j.createdAt), { start, end }),
    );
    const prevCount = journals.filter((j) =>
      isWithinInterval(new Date(j.createdAt), { start: prevStart, end: prevEnd }),
    ).length;

    const count = inWeek.length;
    const daily = eachDayOfInterval({ start, end }).map((day, i) => ({
      label: WEEKDAYS[i],
      count: inWeek.filter((j) => isSameDay(new Date(j.createdAt), day)).length,
    }));

    const tagCounts: Record<string, number> = {};
    inWeek.forEach((j) => {
      const k = coerceJournalTagForDisplay(j.tag);
      tagCounts[k] = (tagCounts[k] ?? 0) + 1;
    });
    const themes = Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a]).slice(0, 3);

    let headline: string;
    let body: string;
    if (count === 0) {
      headline = "A quiet week can be a reset.";
      body = "Whenever you're ready, your next unfolding will be here.";
    } else if (count === 1) {
      headline = "One unfolding, fully felt.";
      body = "A single honest reflection carries further than a handful of hurried ones.";
    } else if (prevCount > 0 && count < prevCount) {
      headline = "A quieter week, but a meaningful reset.";
      body = "You created space to reflect and realign. Your unfoldings were less frequent, but more intentional.";
    } else if (prevCount > 0 && count > prevCount) {
      headline = "A fuller week than the last.";
      body = "You came back to the page more often. The rhythm is building.";
    } else {
      headline = "A steady week of showing up.";
      body = "You kept returning to reflect. That consistency is the practice.";
    }

    const times = (n: number) => (n === 1 ? "time" : "times");
    let pattern: string;
    if (count === 0) pattern = "No unfoldings landed this week. The thread picks up whenever you do.";
    else if (prevCount === 0) pattern = `You unfolded ${count} ${times(count)} this week — a first rhythm to measure against.`;
    else if (count > prevCount) pattern = `You unfolded ${count} ${times(count)} this week, up from ${prevCount} last week.`;
    else if (count < prevCount) pattern = `You unfolded ${count} ${times(count)} this week, down from ${prevCount} last week.`;
    else pattern = `You unfolded ${count} ${times(count)} this week — the same as last week.`;

    let suggestion: string;
    if (count === 0) suggestion = "Start where it's easy — a link you saved, or a thought you keep circling.";
    else if (count === 1) suggestion = "One more this week would be enough for a pattern to start showing.";
    else if (themes.length === 1) suggestion = `Everything pointed at ${themes[0]} this week. A different kind of prompt might open something new.`;
    else suggestion = "Try a 5-minute evening unfolding to carry this momentum forward.";

    return {
      key: format(start, "yyyy-MM-dd"),
      label: `${format(start, "MMM d")} – ${format(end, "MMM d")}`,
      count, daily, themes, headline, body, pattern, suggestion,
    };
  }, [journals, referenceDate]);

  // ── Monthly (local) ───────────────────────────────────────────────────────
  const month = useMemo(() => {
    const start = startOfMonth(referenceDate);
    const end = endOfMonth(referenceDate);
    const inMonth = journals.filter((j) => isSameMonth(new Date(j.createdAt), referenceDate));
    const prevDate = subMonths(referenceDate, 1);
    const prevCount = journals.filter((j) => isSameMonth(new Date(j.createdAt), prevDate)).length;

    const daily = eachDayOfInterval({ start, end }).map((day) => ({
      label: format(day, "d"),
      count: inMonth.filter((j) => isSameDay(new Date(j.createdAt), day)).length,
    }));

    const tagCounts: Record<string, number> = {};
    inMonth.forEach((j) => {
      const k = coerceJournalTagForDisplay(j.tag);
      tagCounts[k] = (tagCounts[k] ?? 0) + 1;
    });
    const themes = Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a]).slice(0, 3);

    const count = inMonth.length;
    const noun = count === 1 ? "unfolding" : "unfoldings";
    const name = format(referenceDate, "MMMM");
    const prevName = format(prevDate, "MMMM");
    // Arithmetic, not authored copy — safe with or without the backend.
    const pattern =
      prevCount === 0
        ? `${count} ${noun} in ${name} — the first month with a record to compare against.`
        : count > prevCount
          ? `${count} ${noun} in ${name}, up from ${prevCount} in ${prevName}.`
          : count < prevCount
            ? `${count} ${noun} in ${name}, down from ${prevCount} in ${prevName}.`
            : `${count} ${noun} in ${name} — the same as ${prevName}.`;

    return {
      key: format(start, "yyyy-MM-01"),
      label: format(referenceDate, "MMMM yyyy"),
      count, daily, themes, pattern,
    };
  }, [journals, referenceDate]);

  const isWeek = granularity === "week";
  const active = isWeek ? week : month;

  // Only the visible granularity fetches.
  const weekly = useAiWeeklyNarrative(week.key, { enabled: isWeek });
  const weeklyAi = weekly.narrative;
  const monthly = useAiMonthlyNarrative(month.key, { enabled: !isWeek && month.count > 0 });

  const showAiBadge = isWeek ? !!weeklyAi : monthly.status === "ready" && !!monthly.narrative;
  // Weekly keeps its local fallback prose, so an unavailable week is not an error
  // state — but "not enough written yet" is worth saying plainly when the backend
  // reports it and the local copy would otherwise imply a full summary.
  const weekInsufficient = isWeek && weekly.status === "insufficient_data" && !weeklyAi;
  const hasEntries = active.count > 0;

  // Week keeps its local fallback prose; month shows model prose only.
  const headline = isWeek ? weeklyAi?.headline ?? week.headline : monthly.narrative?.headline;
  const body = isWeek ? weeklyAi?.body ?? week.body : monthly.narrative?.body;
  const pattern = isWeek ? weeklyAi?.pattern ?? week.pattern : monthly.narrative?.pattern ?? month.pattern;
  const suggestion = isWeek ? weeklyAi?.suggestion ?? week.suggestion : monthly.narrative?.suggestion;
  const showSuggestion = isWeek || (monthly.status === "ready" && !!monthly.narrative?.suggestion);

  return (
    <InsightShell>
      <InsightHeader
        title="Insights"
        showAiBadge={showAiBadge}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        bodyId="insights-body"
        right={
          <div className="relative">
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as Granularity)}
              aria-label="Insight granularity"
              className="cursor-pointer appearance-none rounded-full border border-foreground/10 bg-white/70 py-1 pl-3 pr-7 text-xs font-medium text-foreground/80 transition-colors hover:border-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary/25 sm:text-[13px]"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground/45" />
          </div>
        }
      />

      {/* Period label sits under the title — the calendar decides it, not this card. */}
      <p className="mt-1 text-sm text-muted-foreground">{active.label}</p>

      <div id="insights-body" hidden={collapsed}>
        {!hasEntries ? (
          <div className="mt-5">
            <h3 className="font-display text-xl font-medium leading-snug text-foreground sm:text-2xl">
              {isWeek ? week.headline : `No unfoldings in ${month.label}.`}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {isWeek ? week.body : "A quiet month is still part of the record."}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-center">
              <NarrativeSlot
                isWeek={isWeek}
                status={monthly.status}
                headline={headline}
                body={body}
                periodLabel={active.label}
                minimumEntries={isWeek ? weekly.minimumEntries : monthly.minimumEntries}
                entryCount={isWeek ? week.count : monthly.entryCount ?? month.count}
                weekInsufficient={weekInsufficient}
                onRetry={monthly.retry}
              />
              <TrendChart
                points={active.daily}
                ariaLabel={`Unfoldings per day in ${active.label}`}
                showLabel={isWeek ? undefined : (i) => i % 7 === 0}
              />
            </div>

            <div
              className={cn(
                "mt-6 grid grid-cols-1 gap-5 sm:gap-6",
                showSuggestion ? "sm:grid-cols-3" : "sm:grid-cols-2",
              )}
            >
              <InsightCard
                icon={<LineChart className="h-4 w-4 text-lavender-foreground" />}
                tint="bg-lavender/25"
                title="Pattern"
                body={pattern ?? ""}
              />
              <InsightCard
                icon={<Leaf className="h-4 w-4 text-sage-foreground" />}
                tint="bg-sage/25"
                title="Themes"
                body={
                  active.themes.length > 0
                    ? active.themes.join(" · ")
                    : `Your themes will show up here once you unfold this ${isWeek ? "week" : "month"}.`
                }
                emphasis={active.themes.length > 0}
              />
              {showSuggestion && suggestion && (
                <InsightCard
                  icon={<Lightbulb className="h-4 w-4 text-golden-foreground" />}
                  tint="bg-golden/25"
                  title="Gentle suggestion"
                  body={suggestion}
                />
              )}
            </div>
          </>
        )}
      </div>
    </InsightShell>
  );
}

/** Headline + body. Week always has local copy; month shows prose only when the
 * backend supplied it, and says so plainly otherwise. */
function NarrativeSlot({
  isWeek,
  status,
  headline,
  body,
  periodLabel,
  minimumEntries,
  entryCount,
  weekInsufficient,
  onRetry,
}: {
  isWeek: boolean;
  status: ReturnType<typeof useAiMonthlyNarrative>["status"];
  headline?: string;
  body?: string;
  periodLabel: string;
  minimumEntries: number | null;
  entryCount: number | null;
  weekInsufficient: boolean;
  onRetry: () => void;
}) {
  const shortfall =
    minimumEntries != null && entryCount != null && minimumEntries > entryCount
      ? minimumEntries - entryCount
      : null;

  // Weekly still renders its local copy, plus an honest note when the backend
  // says the week is below the threshold for a generated narrative.
  if (isWeek) {
    return (
      <div>
        <h3 className="font-display text-xl font-medium leading-snug text-foreground sm:text-2xl">
          {headline}
        </h3>
        {body && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>}
        {weekInsufficient && minimumEntries != null && (
          <p className="mt-2 text-xs text-muted-foreground/80">
            {shortfall != null && shortfall > 0
              ? `${shortfall} more ${shortfall === 1 ? "unfolding" : "unfoldings"} this week and we can look for a pattern.`
              : `A written insight needs at least ${minimumEntries} unfoldings in a week.`}
          </p>
        )}
      </div>
    );
  }

  if (status === "ready" && headline) {
    return (
      <div>
        <h3 className="font-display text-xl font-medium leading-snug text-foreground sm:text-2xl">
          {headline}
        </h3>
        {body && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>}
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div aria-busy="true">
        <div className="h-6 w-4/5 animate-pulse rounded-lg bg-foreground/[0.07] sm:h-7" />
        <div className="mt-3 h-3.5 w-full animate-pulse rounded bg-foreground/[0.05]" />
        <div className="mt-2 h-3.5 w-11/12 animate-pulse rounded bg-foreground/[0.05]" />
      </div>
    );
  }

  if (status === "insufficient_data") {
    return (
      <p className="text-sm leading-relaxed text-muted-foreground">
        {shortfall != null && shortfall > 0
          ? `${shortfall} more ${shortfall === 1 ? "unfolding" : "unfoldings"} in ${periodLabel} and we can look for a pattern.`
          : minimumEntries != null
            ? `A written insight for ${periodLabel} needs at least ${minimumEntries} unfoldings.`
            : `Not enough written in ${periodLabel} yet to draw a pattern.`}{" "}
        Your activity is below.
      </p>
    );
  }

  if (status === "error") {
    return (
      <div className="text-sm leading-relaxed text-muted-foreground">
        <p>Couldn't load this month's insight.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1.5 font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/80"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      No written insight for {periodLabel} yet. Your activity is below.
    </p>
  );
}
